import { useState } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { DIMS, PC } from "@/components/compass/dims";

export const COMPASS_COLORS = { deep: PC.deep, green: "#2F8F5B", mint: "#EAF4EC", cream: PC.cream, line: PC.line, muted: PC.muted };

function Illustration({ dim }) {
  const Icon = dim.icon;
  return (
    <div className="relative grid h-24 w-24 shrink-0 place-items-center sm:h-28 sm:w-28" aria-hidden="true">
      <div className="absolute inset-0 rounded-[38%_62%_55%_45%/50%_45%_55%_50%]" style={{ background: `${dim.color}1f` }} />
      <div className="absolute inset-3 rounded-full" style={{ background: `${dim.color}26` }} />
      <Icon className={`relative h-11 w-11 ${dim.anim}`} style={{ color: dim.color }} strokeWidth={1.8} />
    </div>
  );
}

function Item({ it, qid, answers, setAnswers, color }) {
  const key = `${qid}.${it.id}`;
  return (
    <fieldset className="mt-6" data-testid={`compass-item-${key}`}>
      <legend className="text-[15px] font-extrabold" style={{ color: PC.ink }}>{it.label}</legend>
      <div className="mt-3 grid gap-2.5 sm:grid-cols-2">
        {it.options.map((o) => {
          const on = answers[key] === o.v;
          return (
            <button type="button" key={o.v} aria-pressed={on} onClick={() => setAnswers({ ...answers, [key]: on ? undefined : o.v })}
              data-testid={`compass-opt-${key}-${o.v}`}
              className="group flex min-h-[58px] items-center gap-3 rounded-2xl px-4 py-3 text-left text-sm font-semibold transition-[background-color,border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2"
              style={{ background: on ? `${color}14` : "#fff", color: PC.ink, border: `2px solid ${on ? color : PC.line}`, boxShadow: on ? `0 6px 18px ${color}26` : "0 1px 2px rgba(0,0,0,.03)" }}>
              <span className="grid h-6 w-6 shrink-0 place-items-center rounded-full transition-colors" style={{ background: on ? color : "#fff", border: `2px solid ${on ? color : PC.line}` }}>
                {on && <Check className="h-3.5 w-3.5 text-white" strokeWidth={3} />}
              </span>
              {o.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}

export function CompassQuestions({ questions, answers, setAnswers, onDone, onBack, context, busy, index, setIndex }) {
  const [own, setOwn] = useState(0);
  const i = index ?? own, setI = setIndex ?? setOwn;
  const reduce = useReducedMotion();
  const q = questions[i];
  const dim = DIMS.find((d) => d.id === q.id) || DIMS[0];
  const last = i === questions.length - 1;
  const next = () => (last ? onDone() : setI(i + 1));
  const skip = () => { const a = { ...answers }; q.items.forEach((it) => { delete a[`${q.id}.${it.id}`]; }); setAnswers(a); next(); };
  const pad = (n) => String(n).padStart(2, "0");
  return (
    <div data-testid="compass-questions">
      <div className="flex items-center gap-3">
        <span className="font-mono text-sm font-bold" style={{ color: dim.color }} data-testid="compass-progress" aria-label={`Question ${i + 1} of ${questions.length}`}>{pad(i + 1)} / {pad(questions.length)}</span>
        <div className="flex flex-1 gap-1.5" aria-hidden="true">
          {questions.map((qq, k) => <span key={qq.id} className="h-1.5 flex-1 rounded-full transition-colors duration-300" style={{ background: k <= i ? (DIMS.find((d) => d.id === qq.id)?.color || PC.deep) : PC.line }} />)}
        </div>
      </div>
      <AnimatePresence mode="wait" initial={false}>
        <motion.div key={q.id} initial={reduce ? false : { opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} exit={reduce ? { opacity: 1 } : { opacity: 0, x: -24 }} transition={{ duration: 0.25 }}>
          <div className="mt-7 flex items-start gap-5">
            <Illustration dim={dim} />
            <div>
              <p className="text-xs font-extrabold uppercase tracking-[0.14em]" style={{ color: dim.color }}>Dimension {i + 1} · {dim.label}</p>
              <h2 className="mt-1 text-2xl font-extrabold leading-tight tracking-tight sm:text-3xl" style={{ color: PC.ink }} data-testid="compass-question-title">{dim.ask || q.title}</h2>
              <p className="mt-2 text-sm leading-relaxed" style={{ color: PC.muted }}>{dim.why}</p>
            </div>
          </div>
          {context?.[q.id]}
          {q.items.map((it) => <Item key={it.id} it={it} qid={q.id} answers={answers} setAnswers={setAnswers} color={dim.color} />)}
        </motion.div>
      </AnimatePresence>
      <p className="mt-5 text-xs" style={{ color: PC.muted }}>Not sure? Skip it — unanswered items are recorded as "not enough information", never as a problem.</p>
      <div className="mt-6 flex flex-wrap items-center gap-2">
        <button type="button" onClick={() => (i === 0 ? onBack?.() : setI(i - 1))} data-testid="compass-back"
          className="inline-flex min-h-[48px] items-center gap-1.5 rounded-full bg-white px-5 text-sm font-bold" style={{ border: `1.5px solid ${PC.line}`, color: PC.ink }}>
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <button type="button" onClick={skip} data-testid="compass-skip" className="inline-flex min-h-[48px] items-center rounded-full px-4 text-sm font-bold underline-offset-4 hover:underline" style={{ color: PC.muted }}>Skip</button>
        <button type="button" onClick={next} disabled={busy} data-testid="compass-next"
          className="ml-auto inline-flex min-h-[48px] items-center gap-2 rounded-full px-7 text-sm font-extrabold text-white transition-transform hover:-translate-y-0.5 disabled:opacity-60" style={{ background: PC.deep }}>
          {last ? "See my Pet Compass" : "Continue"} <ArrowRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
